/**
 * Conflict-card renderer — surfaces a "two sources disagree" decision.
 *
 * Same shape as entity-card, but specifically for resolving a SINGLE
 * field's value when two ingestion sources returned different values
 * (e.g. FEC says Smith's address is X, the org's directory says Y).
 *
 * The resolution_options array is a list of action keys; the renderer
 * maps each to a button label + primary/secondary style. "manual_entry"
 * triggers a window.prompt() to capture the user's preferred value.
 */

import { registerRenderer, type RendererProps } from "./registry.js";
import { AlertTriangle } from "lucide-react";

export interface ConflictSource {
  source_name?: string;
  value?: unknown;
  fetched_at?: string;
  confidence?: number;
}

export type ConflictResolutionOption =
  | "use_source_a"
  | "use_source_b"
  | "skip_field"
  | "manual_entry"
  | string;

export interface ConflictCardInput {
  field_label?: string;
  entity_label?: string;
  source_a?: ConflictSource;
  source_b?: ConflictSource;
  resolution_options?: ConflictResolutionOption[];
}

function fmt(v: unknown): string {
  if (v === null || v === undefined) return "(none)";
  if (typeof v === "object") return JSON.stringify(v);
  return String(v);
}

function optionMeta(
  opt: string,
  a?: ConflictSource,
  b?: ConflictSource
): { label: string; primary: boolean } {
  switch (opt) {
    case "use_source_a":
      return { label: `Use ${a?.source_name || "Source A"}`, primary: true };
    case "use_source_b":
      return { label: `Use ${b?.source_name || "Source B"}`, primary: false };
    case "skip_field":
      return { label: "Skip this field", primary: false };
    case "manual_entry":
      return { label: "Manual entry", primary: false };
    default:
      return { label: opt, primary: false };
  }
}

function ConflictCard({
  input,
  consumed,
  onConsume,
}: RendererProps<ConflictCardInput>) {
  const a = input.source_a || {};
  const b = input.source_b || {};

  const handleAction = (opt: string) => {
    if (consumed) return;
    if (opt === "manual_entry") {
      const manual = prompt(
        `Manual value for "${input.field_label || ""}" on ${input.entity_label || ""}:`
      );
      if (manual == null) return;
      onConsume?.({
        kind: "conflict_resolution",
        payload: { action: opt, manual_value: manual },
        followupMessage: `conflict resolution: manual_entry for ${input.field_label || ""} on ${input.entity_label || ""} = "${manual}". Continue.`,
      });
      return;
    }
    let meta = "skipped this field";
    if (opt === "use_source_a") {
      meta = `chose ${a.source_name || "A"} (${JSON.stringify(a.value)})`;
    } else if (opt === "use_source_b") {
      meta = `chose ${b.source_name || "B"} (${JSON.stringify(b.value)})`;
    }
    onConsume?.({
      kind: "conflict_resolution",
      payload: { action: opt },
      followupMessage: `conflict resolution: ${meta} for ${input.field_label || ""} on ${input.entity_label || ""}. Continue.`,
    });
  };

  return (
    <div
      className={`self-stretch border border-stone-200 rounded-xl shadow-card bg-white p-3 flex flex-col gap-2.5 ${consumed ? "opacity-60" : ""}`}
    >
      <div className="flex items-center gap-2">
        <AlertTriangle size={15} strokeWidth={1.75} className="text-warn shrink-0" aria-hidden />
        <span className="text-sm font-bold text-stone-900">
          Sources disagree on {input.field_label || ""}
        </span>
      </div>
      <div className="text-xs text-stone-600">
        Entity: <strong className="text-stone-900">{input.entity_label || ""}</strong>
      </div>
      <div className="grid grid-cols-2 gap-2">
        {[a, b].map((src, i) => (
          <div
            key={i}
            className="bg-stone-50 border border-stone-200 rounded-md p-2 flex flex-col gap-1"
          >
            <div className="text-[10px] uppercase tracking-wider font-semibold text-stone-500">
              {src.source_name || (i === 0 ? "Source A" : "Source B")}
            </div>
            <div className="text-xs font-medium text-stone-900 break-words">
              {fmt(src.value)}
            </div>
            <div className="text-[10px] text-stone-500">
              {src.fetched_at ? `fetched ${src.fetched_at}` : ""}
              {src.confidence != null
                ? ` · ${Math.round(src.confidence * 100)}% conf`
                : ""}
            </div>
          </div>
        ))}
      </div>
      {input.resolution_options && input.resolution_options.length > 0 && (
        <div className="flex flex-wrap gap-2">
          {input.resolution_options.map((opt, i) => {
            const meta = optionMeta(opt, a, b);
            return (
              <button
                key={opt + i}
                onClick={() => handleAction(opt)}
                disabled={consumed}
                className={`text-xs font-semibold px-3 py-1.5 rounded-md disabled:opacity-50 disabled:cursor-not-allowed ${meta.primary ? "bg-action hover:bg-action-hover text-white" : "bg-white border border-stone-300 text-stone-800 hover:bg-stone-100"}`}
              >
                {meta.label}
              </button>
            );
          })}
        </div>
      )}
      {consumed && (
        <div className="text-[11px] text-stone-400 italic">resolved</div>
      )}
    </div>
  );
}

registerRenderer<ConflictCardInput>({
  tool: "surface_conflict",
  label: "Two-source conflict resolver with manual override",
  Component: ConflictCard,
});

export { ConflictCard };
